import i18next from 'i18next'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Column } from 'react-table'

import { DateWithToolTip, Flex } from 'components'
import Badge from 'components/Badge'
import Box from 'components/Box'
import Icon from 'components/Icon'
import Text from 'components/Text'
import Tooltip from 'components/Tooltip'

import { LeadTypeColors } from 'modules/Leads/constant'
import { StyledLink } from 'modules/shared/styles'
import { LeadName } from 'modules/Leads/styles'
import { LeadDuplicateDetails } from 'services/types/lead'
import { LeadDuplicateReason, mapDuplicateReason } from 'queries/lead-duplicate'
import { isEmpty } from 'lodash-es'
import { getActivityIcon } from 'modules/Leads/hooks/use-column-definitions'
import { LeadActivityType } from 'types/global'
import { getLeadRoute } from 'utils/url'
import { useReusableTab } from 'hooks/use-reusable-lead-tab'
import { LEAD_DUPLICATE_TAB } from 'utils/constants'

type Props = {
  onInfoClick: (leadId: string) => void
}

const useDuplicatesColumns = ({ onInfoClick }: Props) => {
  const { openInReusableTab } = useReusableTab({ tabName: LEAD_DUPLICATE_TAB })

  const duplicateTableColumns: Column<LeadDuplicateDetails>[] = useMemo(
    () => [
      {
        accessor: 'lead_name',
        width: 200,
        Cell: ({ row }) => {
          const [showTooltip, setShowTooltip] = useState(false)
          const leadNameRef = useRef<HTMLDivElement>(null)

          const leadNameWithLinkComponent = (
            <LeadName ref={leadNameRef}>
              <StyledLink
                to={getLeadRoute({ leadId: row.original.lead_id })}
                onClick={(e) => {
                  e.preventDefault()
                  openInReusableTab(getLeadRoute({ leadId: row.original.lead_id }))
                }}
              >
                {row.original.lead_name}
              </StyledLink>
            </LeadName>
          )

          useEffect(() => {
            if (leadNameRef.current && leadNameRef.current.offsetWidth < leadNameRef.current.scrollWidth) {
              return setShowTooltip((showTooltip) => !showTooltip)
            }
          }, [row.original.lead_name])

          return (
            <>
              {row.original.has_access ? (
                showTooltip ? (
                  <Tooltip position="bottom" textWidth="200px" title={row.original.lead_name}>
                    {leadNameWithLinkComponent}
                  </Tooltip>
                ) : (
                  leadNameWithLinkComponent
                )
              ) : (
                <Tooltip
                  position="bottomLeft"
                  textTransform="none"
                  textWidth="200px"
                  title="potential_duplicate_lead_access_msg"
                >
                  <Box cursor="not-allowed">{row.original.lead_name}</Box>
                </Tooltip>
              )}
            </>
          )
        },
        Header: 'lead_information_title',
        id: 'lead_name',
      },
      {
        accessor: 'lead_type_name',
        width: 100,
        Cell: ({ row }) =>
          row.original.lead_type_name ? (
            <Badge text={row.original.lead_type_name} color={LeadTypeColors[row.original.lead_type_name]} />
          ) : (
            '-'
          ),
        Header: 'type_lbl',
        id: 'lead_type_name',
      },
      {
        accessor: 'lead_owner_name',
        Cell: ({ row }) => {
          if (row.original.lead_owner_team_name) {
            return (
              <Box alignItems="center" display="flex">
                <Icon color="gray5" icon={['far', 'users']} mr="8px" />
                <Text>{row.original.lead_owner_team_name}</Text>
              </Box>
            )
          }

          return row.original.lead_owner_name && row.original.lead_owner_name.trim() ? (
            <Text>{row.original.lead_owner_name}</Text>
          ) : (
            <Text color="red">unassigned_lbl</Text>
          )
        },
        Header: 'lead_owner_title_lbl',
        id: 'lead_owner_name',
      },
      {
        accessor: 'lead_status_name',
        width: 100,
        Cell: ({ row }) => (row.original.lead_status_name ? <Text>{row.original.lead_status_name}</Text> : '-'),
        Header: 'status_lbl',
        id: 'lead_status_name',
      },
      {
        accessor: 'last_activity',
        Cell: ({ row }) => {
          if (!row.original.last_activity || !row.original.last_activity.last_activity_ts) return '-'

          const icon = getActivityIcon(row.original.last_activity.last_activity_type as LeadActivityType)

          return (
            <Flex gap="0.5rem">
              <Icon icon={icon} color="gray5" />
              <DateWithToolTip fontSize={13} fontWeight="medium" date={row.original.last_activity.last_activity_ts} />
            </Flex>
          )
        },
        Header: 'last_activity_title',
        id: 'last_activity',
      },
      {
        accessor: 'last_activity',
        Cell: ({ row }) => {
          if (!row.original.last_activity || !row.original.last_activity.last_activity_status) return '-'

          return row.original.last_activity.last_activity_status
        },
        Header: 'last_activity_outcome',
        id: 'last_activity_outcome',
      },
      {
        accessor: 'lead_in_date',
        Cell: ({ row }) => <DateWithToolTip date={row.original.lead_in_date} dateFormat="PPp" />,
        Header: 'lead_in_date_title',
        id: 'lead_in_date',
      },
      {
        accessor: 'reasons',
        disableSortBy: true,
        Cell: ({ row }) => {
          const isExact = row.original.duplicate_type === 'exact'
          let reasons = ''
          const reasonsData = row.original.reasons

          if (!isEmpty(reasonsData)) {
            const reasonList = Array.isArray(reasonsData)
              ? reasonsData
              : typeof reasonsData === 'object'
              ? Object.values(reasonsData)
              : []

            reasons = reasonList.map((reason) => mapDuplicateReason(reason as LeadDuplicateReason)).join(', ')
          }
          return (
            <Box>
              <Text>
                <Text inline fontWeight="medium">
                  {i18next.t(isExact ? 'duplicate_type_exact' : 'duplicate_type_potential')}
                </Text>{' '}
                {i18next
                  .t('because_of_name', {
                    name: reasons,
                  })
                  .toLocaleLowerCase()}
              </Text>
            </Box>
          )
        },
        Header: 'duplicate_note_lbl',
        id: 'duplicate_note',
      },
      {
        accessor: 'lead_id',
        disableSortBy: true,
        width: 20,
        Cell: ({ row }) => (
          <Flex alignItems="center" justifyContent="center" gap="0.5rem">
            <Icon
              icon={['far', 'circle-exclamation']}
              color="gray5"
              onClick={() => onInfoClick(row.original.lead_id)}
            />
          </Flex>
        ),
        id: 'lead_id',
      },
    ],
    [onInfoClick],
  )

  return { duplicateTableColumns }
}

export default useDuplicatesColumns
